/**
 * ConnectedToPlansCard — Phase 86.D. "Connected to your plans".
 *
 * Shows which accounts, investments and liabilities actually support each goal —
 * the inverse view of MoneyByPurposeCard, built from the same allocation rows so
 * the two can never disagree.
 *
 * A goal can draw on several resources and a resource can serve several goals,
 * so each row states the ALLOCATED amount rather than the resource's full value.
 * Debt-freedom goals measure progress as debt removed, not money accumulated.
 */
import React from 'react';
import { ArrowRight, Landmark, LineChart, CreditCard, Link2Off } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney } from '@/lib/format';

const ROLE_ICON = {
  account:        Landmark,
  wealth_holding: LineChart,
  liability:      CreditCard,
};

const ROLE_LABEL = {
  funding_source:    'Funding source',
  long_term_asset:   'Long-term',
  tracked_liability: 'Tracked debt',
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function GoalBlock({ goal, base, onManage }) {
  const pct = goal.progress_pct;
  const clamped = pct == null ? null : Math.max(Math.min(pct, 100), 0);
  const isDebt = goal.kind === 'debt_freedom';

  return (
    <li className="px-5 py-4" data-testid={`plans-goal-${goal.goal_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-charcoal truncate">{goal.title}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {goal.resource_count === 0
              ? 'Nothing assigned yet'
              : `${goal.resource_count} ${goal.resource_count === 1 ? 'resource' : 'resources'}`}
            {goal.target_date && <> · target {fmtDate(goal.target_date)}</>}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="num text-sm font-medium text-charcoal">
            {formatMoney(goal.funded_amount, base)}
            {goal.target_amount > 0 && (
              <span className="text-muted-foreground font-normal">
                {' '}/ {formatMoney(goal.target_amount, base)}
              </span>
            )}
          </p>
          {pct != null && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {isDebt ? `${Math.round(pct)}% paid down` : `${Math.round(pct)}% funded`}
            </p>
          )}
        </div>
      </div>

      {clamped != null && (
        <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden mt-2.5">
          <div
            className="h-full rounded-full transition-[width] duration-500"
            style={{
              width: `${Math.max(clamped, 1)}%`,
              backgroundColor: isDebt ? '#A84E38' : '#3D7A63',
            }}
          />
        </div>
      )}

      {goal.months_protected != null && (
        <p className="text-[11px] text-muted-foreground mt-2" data-testid={`plans-months-${goal.goal_id}`}>
          Around {goal.months_protected} months of spending protected.
        </p>
      )}

      {goal.resources.length > 0 ? (
        <ul className="mt-3 space-y-1.5">
          {goal.resources.map((r) => {
            const Icon = ROLE_ICON[r.source_table === 'accounts' ? 'account'
              : r.source_table === 'wealth_holdings' ? 'wealth_holding' : 'liability'] || Landmark;
            return (
              <li key={r.link_id} className="flex items-center gap-2.5 text-xs"
                  data-testid={`plans-resource-${r.link_id}`}>
                <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-charcoal truncate flex-1">{r.name}</span>
                {r.allocation_method !== 'entire_resource' && (
                  <span className="text-[10px] text-muted-foreground">
                    {r.allocation_method === 'percentage'
                      ? `${r.allocation_value}% of it`
                      : 'part of it'}
                  </span>
                )}
                <span className="text-[10px] text-muted-foreground/80">
                  {ROLE_LABEL[r.role] || ''}
                </span>
                <span className="num text-charcoal tabular-nums">
                  {formatMoney(r.native_amount, r.currency)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <button
          type="button"
          onClick={onManage}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
          data-testid={`plans-assign-${goal.goal_id}`}
        >
          <Link2Off className="h-3 w-3" /> Assign money to this goal
        </button>
      )}
    </li>
  );
}

export default function ConnectedToPlansCard({ data, loading, onManage }) {
  if (loading && !data) {
    return (
      <section className="bg-card rounded-xl border border-border shadow-sm p-5" data-testid="plans-loading">
        <Skeleton className="h-4 w-48 mb-4" />
        {[0, 1].map((i) => <Skeleton key={i} className="h-16 w-full mb-3" />)}
      </section>
    );
  }
  if (!data || (data.goals || []).length === 0) return null;

  const base = data.base_currency;

  return (
    <section
      className="bg-card rounded-xl border border-border shadow-sm overflow-hidden"
      data-testid="connected-to-plans-card"
    >
      <header className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-charcoal">Connected to your plans</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            How accounts, investments and liabilities support your goals.
          </p>
        </div>
        <button
          type="button"
          onClick={onManage}
          className="text-xs font-medium text-primary hover:underline shrink-0 inline-flex items-center gap-1"
          data-testid="plans-manage"
        >
          Manage <ArrowRight className="h-3 w-3" />
        </button>
      </header>

      <ul className="divide-y divide-border/40">
        {data.goals.map((g) => (
          <GoalBlock key={g.goal_id} goal={g} base={base} onManage={onManage} />
        ))}
      </ul>

      {data.unlinked_count > 0 && (
        <p className="px-5 py-3 text-[11px] text-muted-foreground border-t border-border/60"
           data-testid="plans-unlinked-note">
          {data.unlinked_count} goal{data.unlinked_count === 1 ? '' : 's'} have no money
          assigned yet, so their progress can&rsquo;t be tracked from your accounts.
        </p>
      )}
    </section>
  );
}
